import React, { useCallback, useEffect, useRef, useState } from "react";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK ?? "";

interface ItemRow {
  item:        string;
  unitPrice:   string;
  gst:         string;
  totalValue:  string;
  isInput:     boolean;
  isEmpty?:    boolean;
  isBold?:     boolean;
  isItalic?:   boolean;
  isTotalRow?: boolean;
  isGstRow?:   boolean;
  onBlurItem?: string;
  unitPriceId?: string;
  gstId?:      string;
  totalId?:    string;
}

interface InstallerOption {
  value: string;
  label: string;
}

interface PageData {
  currency:           string;
  fromPage:           string;
  schoolCode:         string;
  familyCode:         string;
  login:              string;
  ddCode:             string;
  name:               string;
  phone:              string;
  address:            string;
  orderDate:          string;
  orderRef:           string;
  orderComments:      string;
  distributorName:    string;
  displayInstaller:   string;
  displayInstDateTime:string;
  approved:           boolean;
  txtOrderComments:   string;
  installationDate:   string;
  installationHH:     string;
  installationMM:     string;
  installationAMPM:   string;
  installerLogin:     string;
  installerOptions:   InstallerOption[];
  items:              ItemRow[];
  grandTotal:         number;
  totalGst:           number;
  showCourierDetails: boolean;
  showApprove:        boolean;
  errorMsg:           string;
}


/** mirrors gfn_Trim */
function gfn_Trim(s: string): string { return (s ?? "").trim(); }

/** mirrors gfn_IsNumeric(pInp, canHavePlusMinus, canHaveDot) */
function gfn_IsNumeric(pInp: string, canHavePlusMinus: boolean, canHaveDot: boolean): boolean {
  let j = 0, PlusMinus = "", Dot = "";
  for (let i = 0; i < pInp.length; i++) {
    const ch = pInp.charAt(i);
    if (ch === "+" || ch === "-") {
      if (!canHavePlusMinus || PlusMinus !== "" || j > 0) return false;
      PlusMinus = ch;
    } else if (ch === ".") {
      if (!canHaveDot || Dot !== "") return false;
      Dot = ch;
    } else if (ch < "0" || ch > "9") {
      return false;
    } else {
      j++;
    }
  }
  return true;
}

/** mirrors gfn_IsValidDate(pInp) — expects dd/MM/yyyy */
function gfn_IsValidDate(pInp: string): boolean {
  if (pInp.length !== 10) return false;
  const ddc = pInp.substr(0, 2), mmc = pInp.substr(3, 2), yyc = pInp.substr(6, 4);
  const ch3 = pInp.charAt(2), ch6 = pInp.charAt(5);
  const dd = parseInt(ddc, 10), mm = parseInt(mmc, 10), yy = parseInt(yyc, 10);
  if (isNaN(dd) || isNaN(mm) || isNaN(yy)) return false;
  if ((ch3 !== "/" && ch3 !== "-" && ch3 !== ".") || (ch6 !== "/" && ch6 !== "-" && ch6 !== ".")) return false;
  if (dd < 1 || mm < 1 || mm > 12 || yy < 1900) return false;
  if ([1, 3, 5, 7, 8, 10, 12].includes(mm)) { if (dd > 31) return false; }
  else if ([4, 6, 9, 11].includes(mm)) { if (dd > 30) return false; }
  else {
    const leap = (yy % 4 === 0 && yy % 100 !== 0) || yy % 400 === 0;
    if ((leap && dd > 29) || (!leap && dd > 28)) return false;
  }
  return true;
}

/** mirrors gfn_ShowMessage(msg, type) — simplified */
function gfn_ShowMessage(msg: string, type?: string) {
  if (msg && msg.trim()) alert(msg);
}

const css = `
  * { box-sizing: border-box; }
  body { font-family: Arial, sans-serif; font-size: 9pt; margin: 0; padding: 0; }

  #div1 { padding: 5px; width: 98%; }

  .page-title {
    font-family: Arial; font-size: 12pt; color: red;
    text-align: center; font-weight: bold; padding: 7px;
  }

  /* Details table */
  #tblDetails {
    width: 100%; border: 1px solid black;
    border-collapse: collapse;
    font-family: Arial; font-size: 9pt;
  }
  #tblDetails td {
    padding: 5px; vertical-align: top;
    width: 50%;
  }

  /* Items + right panel outer table */
  .outer-panel {
    width: 100%;
    border: 1px solid black;
    border-collapse: collapse;
  }
  .outer-panel > tbody > tr > td {
    vertical-align: top;
    padding: 5px;
    width: 50%;
  }
  .outer-panel > tbody > tr > td:first-child {
    border-right: 1px solid black;
    padding-right: 8px;
    padding-bottom: 8px;
  }

  /* Right sub-tables (Courier / Approve) */
  .right-table {
    width: 100%;
    font-family: Arial; font-size: 10pt;
    border-collapse: collapse;
  }
  .right-table td { padding: 5px; vertical-align: top; }
  .right-table td:first-child { font-weight: bold; white-space: nowrap; }

  /* Grid / items table (mirrors dl_divScrollableTable) */
  #dl_divScrollableTable {
    height: 400px;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
  }

  .grid-table {
    width: 100%;
    font-family: Arial; font-size: 9pt;
    border-collapse: collapse;
    border: 1px solid #aaa;
  }
  .grid-table th {
    background-color: #2f6089;
    color: white;
    font-weight: normal;
    font-size: 8pt;
    padding: 4px;
    text-align: left;
    border-bottom: 1px solid #aaa;
  }
  .grid-table th.right { text-align: right; }
  .grid-table td {
    padding: 4px;
    border-bottom: 1px solid #ddd;
    vertical-align: middle;
    font-size: 8pt;
  }
  .grid-table tr:nth-child(even) { background-color: #f5f5f5; }
  .grid-table td.right { text-align: right; }

  /* Editable price inputs in grid */
  .price-input {
    font-weight: bold;
    width: 85px;
    font-family: Arial; font-size: 8pt;
    border: 1px solid #ccc;
    padding: 2px;
  }
  .price-input:disabled { background: #f0f0f0; }

  /* Buttons */
  .btn {
    width: 60px; cursor: pointer;
    font-family: Arial; font-size: 9pt;
    padding: 2px 4px;
  }
  .btn-wide { width: 100px; }

  /* TextBox */
  .txtbox {
    width: 220px;
    font-family: Arial; font-size: 9pt;
    border: 1px solid #ccc; padding: 2px;
  }
  .txtbox-date { width: 138px; }
  .txtbox-time { width: 40px; }
  .txtbox-ml { height: 80px; resize: vertical; }
  .txtbox-comments { width: 220px; height: 80px; resize: vertical; }

  /* Loading / error */
  .loading-msg { padding: 20px; font-family: Arial; font-size: 10pt; color: #555; }
  .error-msg   { padding: 20px; font-family: Arial; font-size: 10pt; color: red; }
`;

// ─── Main Component ────────────────────────────────────────────────────────────
export default function EL_DeliveryDocketDetails() {

  // Read query string — mirrors Request.QueryString
  const qp        = new URLSearchParams(window.location.search);
  // const ddCode    = qp.get("DDCode")     ?? "";
  // const schoolCode= qp.get("SchoolCode") ?? "";
  // const familyCode= qp.get("FamilyCode") ?? "";
  // const login     = qp.get("Login")      ?? "";
  // const fromPage  = qp.get("FromPage")   ?? "";
  
  // "Approve" | "Dockets"
  const ddCode    = qp.get("DDCode")     ?? "";
  const schoolCode= qp.get("SchoolCode") ?? "";
  const familyCode= qp.get("FamilyCode") ?? "";
  const login     = qp.get("Login")      ?? "";
  const fromPage  = qp.get("FromPage")   ?? "";   // "Approve" | "Dockets"

  // ── Data state ───────────────────────────────────────────────────────────────
  const [pageData,     setPageData]     = useState<PageData | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [errorMsg,     setErrorMsg]     = useState("");
  const [submitting,   setSubmitting]   = useState(false);

  // ── Courier Details form state (divCourierDetails) ───────────────────────────
  const [courierRef,        setCourierRef]        = useState("");
  const [courierName,       setCourierName]        = useState("");
  const [deliveryComments,  setDeliveryComments]   = useState("");

  // ── Approve form state (divApprove) ──────────────────────────────────────────
  const [approved,          setApproved]           = useState(false);
  const [installerLogin,    setInstallerLogin]      = useState("");
  const [instDate,          setInstDate]            = useState("");   // dd/MM/yyyy
  const [instHH,            setInstHH]              = useState("");
  const [instMM,            setInstMM]              = useState("");
  const [instAMPM,          setInstAMPM]            = useState("AM");
  const [orderComments,     setOrderComments]       = useState("");

  // ── Editable price inputs (AU path) — mirrors fn_GST state ──────────────────
  // Key = field ID, value = current string value
  const [inputVals, setInputVals] = useState<Record<string, string>>({});

  // ── Grand total / GST display state ──────────────────────────────────────────
  const [grandTotal, setGrandTotal] = useState<string>("");
  const [totalGST,   setTotalGST]   = useState<string>("");

  // Refs for focus
  const instDateRef = useRef<HTMLInputElement>(null);
  const instHHRef   = useRef<HTMLInputElement>(null);
  const instMMRef   = useRef<HTMLInputElement>(null);

  // ── Fetch page data on mount — mirrors Page_Load (IsPostBack=False) ──────────
  useEffect(() => {
    const params = new URLSearchParams({ DDCode: ddCode, SchoolCode: schoolCode, FamilyCode: familyCode, Login: login, FromPage: fromPage });
    fetch(`${API_BASE_URL}api/delivery-docket/?${params.toString()}`)
      .then(r => r.json())
      .then((d: PageData) => {
        setPageData(d);

        // mirrors: If "<%=ErrorMsg %>" != "" Then gfn_ShowMessage(...)
        if (d.errorMsg) gfn_ShowMessage(d.errorMsg, "E");

        // Pre-fill Approve form fields from backend
        setApproved(d.approved);
        setInstallerLogin(d.installerLogin);
        setInstDate(d.installationDate);
        setInstHH(d.installationHH);
        setInstMM(d.installationMM);
        setInstAMPM(d.installationAMPM || "AM");
        setOrderComments(d.txtOrderComments);

        // Grand Total / GST from backend
        const cur = d.currency || "AUD";
        setGrandTotal(`${cur} ${d.grandTotal.toFixed(2)}`);
        setTotalGST(`${cur} ${d.totalGst.toFixed(2)}`);

        // Initialise input field values from items
        const init: Record<string, string> = {};
        for (const item of (d.items || [])) {
          if (item.isInput) {
            if (item.unitPriceId) init[item.unitPriceId] = item.unitPrice;
            if (item.gstId)       init[item.gstId]       = item.gst;
            if (item.totalId)     init[item.totalId]      = item.totalValue;
          }
        }
        setInputVals(init);
      })
      .catch(() => setErrorMsg("Failed to load page."))
      .finally(() => setLoading(false));

    // mirrors: window.setTimeout(fn_ListDimension, 100)
    setTimeout(fn_ListDimension, 100);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── fn_ListDimension — mirrors inline JS fn_ListDimension() ─────────────────
  function fn_ListDimension() {
    const el = document.getElementById("dl_divScrollableTable");
    if (el) {
      el.style.height = "400px";
    }
  }

  // ── fn_GST — mirrors inline JS fn_GST(Item) ──────────────────────────────────
  // Recalculates GST + Total for a price field change, updates Grand Total + Total GST.
  const fn_GST = useCallback((item: string, newUnitPriceStr: string) => {
    if (!gfn_IsNumeric(newUnitPriceStr, false, true)) {
      alert("Please enter valid number");
      return;
    }

    const unitPrice = parseFloat(newUnitPriceStr) || 0;
    const gst       = unitPrice / 12;
    const total     = unitPrice + gst;
    const gstFixed  = parseFloat(gst.toFixed(2));
    const totFixed  = parseFloat(total.toFixed(2));

    // mirrors: PriceArray = gfn_Trim(txtGrTotal.value).split(" "); GRTotal = PriceArray[1]
    const grParts    = gfn_Trim(grandTotal).split(" ");
    const gstParts   = gfn_Trim(totalGST).split(" ");
    const currency   = pageData?.currency || "AUD";
    let grTotal      = parseFloat(grParts[1]  || "0") || 0;
    let curTotalGST  = parseFloat(gstParts[1] || "0") || 0;

    setInputVals(prev => {
      const next = { ...prev };

      // Determine field IDs based on item name (mirrors VB switch logic)
      let unitId = "", gstId = "", totalId = "";
      if      (item === "ExtraHardlocks")  { unitId = "txtExtraHardlocksPrice"; gstId = "txtExtraHardLocksGST";    totalId = "txtExtraHardLocksTotal"; }
      else if (item === "Computer")        { unitId = "txtComputerPrice";        gstId = "txtComputerGST";          totalId = "txtCompuetrTotal"; }
      else if (item === "Installation")    { unitId = "txtInstallationFee";      gstId = "txtInstallationGST";      totalId = "txtInstallationTotal"; }
      else if (item === "Advertisement")   { unitId = "txtAdvertisementFee";     gstId = "txtAdvertisementGST";     totalId = "txtAdvertisementTotal"; }
      else if (item === "Telemarketing")   { unitId = "txtTelemarketingFee";     gstId = "txtTelemarketingGST";     totalId = "txtTelemarketingTotal"; }
      else if (item === "FrieghtCharge")   { unitId = "txtFrieghtCharge";        gstId = "txtFrieghtGST";           totalId = "txtFrieghtTotal"; }

      const prevTotal = parseFloat(next[totalId] || "0") || 0;
      const prevGST   = parseFloat(next[gstId]   || "0") || 0;

      // mirrors: GRTotal = parseFloat(GRTotal - prevTotal + totFixed)
      grTotal     = parseFloat((grTotal    - prevTotal + totFixed).toFixed(2));
      curTotalGST = parseFloat((curTotalGST - prevGST + gstFixed).toFixed(2));

      next[unitId]  = newUnitPriceStr;
      next[gstId]   = gstFixed.toFixed(2);
      next[totalId] = totFixed.toFixed(2);

      return next;
    });

    setGrandTotal(`${pageData?.currency || "AUD"} ${grTotal.toFixed(2)}`);
    setTotalGST(`${pageData?.currency || "AUD"} ${curTotalGST.toFixed(2)}`);
  }, [grandTotal, totalGST, pageData]);

  // ── fn_PrintOrder — mirrors inline JS fn_PrintOrder() ───────────────────────
  // mirrors: gfn_StartNewWindow(url, "ret", 600, 800)
  function fn_PrintOrder() {
    const url = `/SALES/EL_Print_DeliveryDocketDetails?SchoolCode=${schoolCode}&FamilyCode=${familyCode}&Login=${login}&DDCode=${ddCode}`;
    window.open(url, "ret", "width=600,height=800,resizable=yes,scrollbars=yes,menubar=0,toolbar=0,location=0,status=0");
  }

  // ── fn_CheckDate — mirrors inline JS fn_CheckDate(Obj) ──────────────────────
  function fn_CheckDate(val: string): boolean {
    if (val !== "") {
      if (!gfn_IsValidDate(val)) {
        alert("Please enter valid date (mm/dd/yyyy)");
        instDateRef.current?.focus();
        return false;
      }
    }
    return true;
  }

  // ── fn_Date keypress — mirrors: if (keyCode < 47 || > 57) returnValue=false ──
  function fn_DateKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.charCode < 47 || e.charCode > 57) e.preventDefault();
  }

  // ── fn_Time keypress — mirrors: if (keyCode < 48 || > 57) returnValue=false ──
  function fn_TimeKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.charCode < 48 || e.charCode > 57) e.preventDefault();
  }

  // ── fn_Send — mirrors: function fn_Send() {} (empty in VB) ──────────────────
  function fn_Send(): boolean { return true; }

  // ── fn_Save validation — mirrors inline JS fn_Save() ─────────────────────────
  function fn_Save(): boolean {
    // mirrors: if (date == "" && HH != "" && MM != "") → "Please enter installation Date"
    if (!instDate && instHH !== "" && instMM !== "") {
      alert("Please enter installation Date");
      instDateRef.current?.focus();
      return false;
    }
    // mirrors: if (HH == "" && MM != "") → "Please enter installation hour"
    if (instHH === "" && instMM !== "") {
      alert("Please enter installation hour");
      instHHRef.current?.focus();
      return false;
    }
    // mirrors: if (HH != "" && (HH < 1 || HH > 12)) → "Invalid installation hour"
    if (instHH !== "") {
      const hh = parseInt(instHH, 10);
      if (hh < 1 || hh > 12) {
        alert("Invalid installation hour (between 1 and 12)");
        instHHRef.current?.focus();
        return false;
      }
    }
    // mirrors: if (HH != "" && MM == "") → "Please enter installation minutes"
    if (instHH !== "" && instMM === "") {
      alert("Please enter installation minutes");
      instMMRef.current?.focus();
      return false;
    }
    // mirrors: if (MM != "" && (MM < 0 || MM > 59)) → "Invalid installation minutes"
    if (instMM !== "") {
      const mm = parseInt(instMM, 10);
      if (mm < 0 || mm > 59) {
        alert("Invalid installation minutes (between 0 and 59)");
        instMMRef.current?.focus();
        return false;
      }
    }
    return true;
  }

  // ── btnSend_Click — mirrors VB btnSend_Click ──────────────────────────────────
  async function handleSend() {
    if (!fn_Send()) return;
    setSubmitting(true);
    try {
      const resp   = await fetch(`${API_BASE_URL}api/delivery-docket/send/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schoolCode, familyCode, login, ddCode,
          courierRef, courierName, deliveryComments,
        }),
      });
      const result = await resp.json();
      if (result.success) {
        window.location.href = result.redirect;
      } else {
        gfn_ShowMessage(result.error || "Error saving.", "E");
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── btnBack_Click — mirrors: Response.Redirect("~/SALES/Dockets/EL_DeliveryDockets.aspx") ──
  function handleBack() {
    window.location.href = "/SALES/Dockets/EL_DeliveryDockets";
  }

  // ── btnGoBack_Click — mirrors: Response.Redirect("~/DEALER/EL_ApproveDeliveryDockets.aspx") ──
  function handleGoBack() {
    window.location.href = "/DEALER/EL_ApproveDeliveryDockets";
  }

  // ── btnSave_Click — mirrors VB btnSave_Click ──────────────────────────────────
  async function handleSave() {
    if (!fn_Save()) return;
    setSubmitting(true);
    try {
      const resp   = await fetch(`${API_BASE_URL}api/delivery-docket/save-approval/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schoolCode, familyCode, login, ddCode,
          approved, orderComments,
          installerLogin,
          installationDate: instDate,
          installationHH:   instHH,
          installationMM:   instMM,
          installationAMPM: instAMPM,
        }),
      });
      const result = await resp.json();
      if (result.success) {
        window.location.href = result.redirect;
      } else {
        gfn_ShowMessage(result.error || "Error saving.", "E");
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  if (loading) return (
    <>
      <style>{css}</style>
      <div className="loading-msg">Loading...</div>
    </>
  );

  if (!pageData) return (
    <>
      <style>{css}</style>
      <div className="error-msg">{errorMsg || "Failed to load page."}</div>
    </>
  );

  const d = pageData;

  return (
    <>
      <style>{css}</style>

      <div id="div1">

        {/* mirrors: <table><tr><td align="center" style="color:Red;font-family:Arial;font-size:12pt"><b>Delivery Docket Details</b> */}
        <table cellPadding={7} cellSpacing={0} border={0} width="100%">
          <tbody>
            <tr>
              <td className="page-title">
                <b>Delivery Docket Details</b>
              </td>
            </tr>
          </tbody>
        </table>

        <br />

        {/* ── Details table — mirrors tblDetails ── */}
        <div style={{ width: "100%" }}>
          <table id="tblDetails" cellPadding={5} cellSpacing={0} border={0} width="100%">
            <tbody>
              <tr>
                {/* mirrors: Name: <%=Name%> */}
                <td style={{ width: "50%" }}>Name:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {d.name}</td>
                {/* mirrors: Distributor: <b><%=DistributorName%></b> */}
                <td style={{ width: "50%" }}>Distributor:&nbsp;&nbsp;&nbsp; <b>{d.distributorName}</b></td>
              </tr>
              <tr>
                {/* mirrors: Phone: <%=Phone%> */}
                <td>Phone:&nbsp;&nbsp;&nbsp;&nbsp; {d.phone}</td>
                {/* mirrors: Order Date: <b><%=OrderDate%></b> */}
                <td>Order Date:&nbsp;&nbsp;<b>{d.orderDate}</b></td>
              </tr>
              <tr>
                {/* mirrors: Address: <%=Address%> — newlines rendered as <br/> */}
                <td valign="top">
                  Address:&nbsp;
                  {d.address.split("\n").map((line, i) => (
                    <span key={i}>{line}{i < d.address.split("\n").length - 1 && <br />}</span>
                  ))}
                </td>
                {/* mirrors: Order Ref.: <b><%=OrderRef%></b> */}
                <td valign="top">Order Ref.:&nbsp;&nbsp;&nbsp; <b>{d.orderRef}</b></td>
              </tr>
            </tbody>
          </table>
        </div>

        <br />

        {/* ── Items + Right panel — mirrors the outer 2-column table ── */}
        <div style={{ width: "100%", textAlign: "left" }}>
          <table width="100%" cellPadding={5} cellSpacing={0} border={0}
            style={{ border: "1px solid black" }}>
            <tbody>
              <tr>
                {/* ── LEFT: Items grid — mirrors <%=DisplayItems%> / dl_divScrollableTable ── */}
                <td valign="top" style={{
                  width: "50%",
                  borderRight: "1px solid black",
                  paddingRight: 8,
                  paddingBottom: 8,
                }}>
                  <div id="dl_divScrollableTable">
                    <table className="grid-table">
                      <thead>
                        <tr>
                          {/* mirrors: Header() = {"Item", "Unit Price", "G.S.T", "Total Value"}
                              Align() = {"left", "right", "right", "right"} */}
                          <th>Item</th>
                          <th className="right">Unit Price</th>
                          <th className="right">G.S.T</th>
                          <th className="right">Total Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.items.map((row, idx) => {
                          if (row.isEmpty) {
                            return <tr key={idx}><td colSpan={4}>&nbsp;</td></tr>;
                          }
                          return (
                            <tr key={idx}>
                              <td>
                                {row.isItalic ? <i>{row.item}</i> : row.item}
                              </td>
                              {/* Unit Price cell */}
                              <td className="right">
                                {row.isInput && row.unitPriceId ? (
                                  <input
                                    id={row.unitPriceId}
                                    type="text"
                                    className="price-input"
                                    value={inputVals[row.unitPriceId] ?? row.unitPrice}
                                    onChange={e => setInputVals(p => ({ ...p, [row.unitPriceId!]: e.target.value }))}
                                    onBlur={e => {
                                      if (row.onBlurItem) fn_GST(row.onBlurItem, e.target.value);
                                    }}
                                    maxLength={12}
                                  />
                                ) : (
                                  <b dangerouslySetInnerHTML={{ __html: row.unitPrice }} />
                                )}
                              </td>
                              {/* GST cell */}
                              <td className="right">
                                {row.isInput && row.gstId ? (
                                  <input
                                    id={row.gstId}
                                    type="text"
                                    className="price-input"
                                    value={inputVals[row.gstId] ?? row.gst}
                                    disabled
                                    maxLength={12}
                                    readOnly
                                  />
                                ) : (
                                  <b dangerouslySetInnerHTML={{ __html: row.gst }} />
                                )}
                              </td>
                              {/* Total Value cell */}
                              <td className="right">
                                {row.isInput && row.totalId ? (
                                  row.isTotalRow || row.isGstRow ? (
                                    // Grand Total / GST rows use live state
                                    <input
                                      id={row.totalId}
                                      type="text"
                                      className="price-input"
                                      value={row.isTotalRow ? grandTotal : totalGST}
                                      disabled
                                      maxLength={12}
                                      readOnly
                                    />
                                  ) : (
                                    <input
                                      id={row.totalId}
                                      type="text"
                                      className="price-input"
                                      value={inputVals[row.totalId] ?? row.totalValue}
                                      disabled
                                      maxLength={12}
                                      readOnly
                                    />
                                  )
                                ) : (
                                  row.isBold
                                    ? <b>{row.totalValue}</b>
                                    : row.isItalic
                                      ? <i>{row.totalValue}</i>
                                      : <b dangerouslySetInnerHTML={{ __html: row.totalValue }} />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </td>

                {/* ── RIGHT: Courier Details + Approve panels ── */}
                <td valign="top" style={{ width: "50%" }}>

                  {/* ── divCourierDetails — mirrors: Visible toggled by FromPage ── */}
                  {/* mirrors: If FromPage = "Approve" Then divCourierDetails.Visible = False */}
                  {d.showCourierDetails && (
                    <div id="divCourierDetails" style={{ width: "100%" }}>
                      <table className="right-table">
                        <tbody>
                          <tr>
                            <td valign="top"><b>Order Comments:</b></td>
                            {/* mirrors: <%=ordercomments%> */}
                            <td>{d.orderComments}</td>
                          </tr>
                          <tr>
                            <td><b>Installer:</b></td>
                            {/* mirrors: <%=DisplayInstaller%> */}
                            <td>{d.displayInstaller}</td>
                          </tr>
                          <tr>
                            <td valign="top"><b>Installation Date &amp; Time:</b></td>
                            {/* mirrors: <%=DisplayInstDateTime%> */}
                            <td>{d.displayInstDateTime}</td>
                          </tr>
                          <tr>
                            <td valign="top"><b>Courier Ref.</b></td>
                            {/* mirrors: <asp:TextBox ID="txtCourierRef" Width="220px" MaxLength="50"> */}
                            <td>
                              <input
                                id="txtCourierRef"
                                type="text"
                                className="txtbox"
                                maxLength={50}
                                value={courierRef}
                                onChange={e => setCourierRef(e.target.value)}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td valign="top"><b>Courier Name</b></td>
                            {/* mirrors: <asp:TextBox ID="txtCourierName" Width="220px" MaxLength="50"> */}
                            <td>
                              <input
                                id="txtCourierName"
                                type="text"
                                className="txtbox"
                                maxLength={50}
                                value={courierName}
                                onChange={e => setCourierName(e.target.value)}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td valign="top"><b>Delivery comments</b></td>
                            {/* mirrors: <asp:TextBox TextMode="MultiLine" Height="80px" Width="220px"> */}
                            <td>
                              <textarea
                                id="txtDeliveryComments"
                                className="txtbox-comments"
                                value={deliveryComments}
                                onChange={e => setDeliveryComments(e.target.value)}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td />
                            <td>
                              <br /><br />
                              {/* mirrors: <asp:Button ID="btnSend" Text="Send" Width="60px" OnClientClick="return fn_Send()" /> */}
                              <button
                                id="btnSend"
                                className="btn"
                                onClick={handleSend}
                                disabled={submitting}
                              >
                                Send
                              </button>
                              &nbsp;&nbsp;
                              {/* mirrors: <asp:Button ID="btnBack" Text="Back" Width="60px" /> */}
                              <button id="btnBack" className="btn" onClick={handleBack}>
                                Back
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* ── divApprove — mirrors: If FromPage = "Dockets" → divApprove.Visible = False ── */}
                  {d.showApprove && (
                    <div id="divApprove" style={{ width: "100%" }}>
                      <table className="right-table">
                        <tbody>
                          <tr>
                            <td valign="top"><b>Approval:</b></td>
                            {/* mirrors: <asp:CheckBox ID="chkApprove" /> */}
                            <td>
                              <input
                                id="chkApprove"
                                type="checkbox"
                                checked={approved}
                                onChange={e => setApproved(e.target.checked)}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td valign="top"><b>Installer:</b></td>
                            {/* mirrors: <asp:DropDownList ID="ddlInstaller" Width="220px"> */}
                            <td>
                              <select
                                id="ddlInstaller"
                                className="txtbox"
                                value={installerLogin}
                                onChange={e => setInstallerLogin(e.target.value)}
                              >
                                {d.installerOptions.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                          <tr>
                            <td valign="top"><b>Installation Date:</b></td>
                            {/* mirrors: <asp:TextBox ID="txtAInstallationDate" Width="138px" onblur="fn_CheckDate" onKeyPress="fn_Date"> */}
                            <td>
                              <input
                                id="txtAInstallationDate"
                                ref={instDateRef}
                                type="text"
                                className="txtbox txtbox-date"
                                value={instDate}
                                onChange={e => setInstDate(e.target.value)}
                                onBlur={e => fn_CheckDate(e.target.value)}
                                onKeyPress={fn_DateKeyPress}
                                placeholder="dd/MM/yyyy"
                              />
                              <span style={{ fontSize: "9pt" }}>&nbsp;(dd/MM/yyyy)</span>
                            </td>
                          </tr>
                          <tr>
                            <td valign="top"><b>Installation Time:</b></td>
                            <td>
                              <span style={{ fontSize: "9pt" }}>
                                {/* mirrors: txtAInstallationHH + txtAInstallationMM + ddlAAMPM */}
                                <input
                                  id="txtAInstallationHH"
                                  ref={instHHRef}
                                  type="text"
                                  className="txtbox txtbox-time"
                                  maxLength={2}
                                  value={instHH}
                                  onChange={e => setInstHH(e.target.value)}
                                  onKeyPress={fn_TimeKeyPress}
                                />
                                {" "}
                                <input
                                  id="txtAInstallationMM"
                                  ref={instMMRef}
                                  type="text"
                                  className="txtbox txtbox-time"
                                  maxLength={2}
                                  value={instMM}
                                  onChange={e => setInstMM(e.target.value)}
                                  onKeyPress={fn_TimeKeyPress}
                                />
                                {" "}
                                {/* mirrors: <asp:DropDownList ID="ddlAAMPM"> AM / PM */}
                                <select
                                  id="ddlAAMPM"
                                  value={instAMPM}
                                  onChange={e => setInstAMPM(e.target.value)}
                                >
                                  <option value="AM">AM</option>
                                  <option value="PM">PM</option>
                                </select>
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; (HH:MM)
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ verticalAlign: "top" }}><b>Order Comments:</b></td>
                            {/* mirrors: <asp:TextBox ID="txtOrderComments" Width="220px" Height="80px"> */}
                            <td>
                              <textarea
                                id="txtOrderComments"
                                className="txtbox-comments"
                                value={orderComments}
                                onChange={e => setOrderComments(e.target.value)}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td />
                            <td>
                              <br /><br />
                              {/* mirrors: <asp:Button ID="btnSave" Text="Save" Width="100px" OnClientClick="return fn_Save()" /> */}
                              <button
                                id="btnSave"
                                className="btn btn-wide"
                                onClick={handleSave}
                                disabled={submitting}
                              >
                                Save
                              </button>
                              &nbsp;&nbsp;
                              {/* mirrors: <input id="btnPrintOrder" type="button" value="Print Order" onclick="fn_PrintOrder()"> */}
                              <input
                                id="btnPrintOrder"
                                type="button"
                                value="Print Order"
                                className="btn btn-wide"
                                onClick={fn_PrintOrder}
                              />
                              &nbsp;&nbsp;
                              {/* mirrors: <asp:Button ID="btnGoBack" Text="Back" Width="100px" /> */}
                              <button id="btnGoBack" className="btn btn-wide" onClick={handleGoBack}>
                                Back
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <br />
      </div>
    </>
  );
}